/**
 *  @swagger
 *  definitions:
 *    RegattaSubscriptionParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        CrewmemberId:
 *          description: ID of the associated Crewmember
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    RegattaSubscriptionCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaSubscriptionParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - CrewmemberId
 *
 *  @swagger
 *  definitions:
 *    RegattaSubscriptionQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaSubscription
 *              type: string
 *        - $ref: '#/definitions/RegattaSubscriptionParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaSubscriptionUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaSubscriptionQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaSubscription:
 *      allOf:
 *        - $ref: '#/definitions/RegattaSubscriptionCreateParams'
 *        - $ref: '#/definitions/RegattaSubscriptionUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaSubscription:
 *        allOf:
 *          - $ref: '#/definitions/RegattaSubscription'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaSubscription
 *                $ref: '#/components/schemas/Regatta'
 *              Crewmember:
 *                description: Crewmember of the RegattaSubscription
 *                $ref: '#/components/schemas/Crewmember'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  CrewmemberId: string;
  RegattaId: string;
};

export type $DTO = Record<string, never>;

export type $CreateParams = $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$RelationIds>;

export type $Associations = {
  Crewmember?: $Model<'Crewmember'>;
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaSubscription'> => {
  const RegattaSubscription = stack.define(
    'RegattaSubscription',
    fields(Sequelize),
    {
      tableName: 'RegattaSubscriptions',
    },
  );

  RegattaSubscription.associate = (models: $ModelClasses) => {
    models.RegattaSubscription.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
    models.RegattaSubscription.belongsTo(
      models.Crewmember,
      {
        as: 'Crewmember',
        foreignKey: 'CrewmemberId',
      },
    );
  };

  RegattaSubscription.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaSubscription;
});
